// Internal tile drag: dnd-kit event handlers that translate pointer deltas
// into snapped-cell layout previews. The active tile itself follows the pointer
// via the dnd-kit translate transform on its item View; this hook only drives
// the landing-cell preview (and the final commit target).
//
// Live sibling reflow is OPT-IN (hold Cmd/Alt — see use-reflow-modifier.ts).
// Without it the preview keeps siblings at their start positions and only the
// active item's rect tracks the settled landing cell (so the target outline is
// truthful); the full reflow is computed once on drop and committed.
//
// NOTE: `event.delta` from dnd-kit is ALREADY scroll-adjusted (dnd-kit tracks
// scrollable ancestors and folds their scroll into the delta) — never add a
// manual scroll correction here or auto-scrolled drags race at 2× speed.

import * as React from 'react';
import type {
  DragEndEvent,
  DragMoveEvent,
  DragStartEvent,
} from '@dnd-kit/core';

import { subgridZoneOver } from './grid-dnd-scope';
import { clamp, layoutBottom, moveItem } from './grid-layout';
import { pitchX, pitchY } from './grid-geometry';
import type { GridGestureCore } from './grid-gesture';
import { reflowModifierHeld, useReflowModifier } from './use-reflow-modifier';

export interface GridDragHandlers {
  onDragStart: (event: DragStartEvent) => void;
  onDragMove: (event: DragMoveEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onDragCancel: () => void;
}

export function useGridDrag(core: GridGestureCore): GridDragHandlers {
  const reflowHeldRef = React.useRef(false);

  const applyPreview = React.useCallback(
    (cell: { x: number; y: number }) => {
      const gesture = core.gestureRef.current;
      if (gesture?.mode !== 'drag') return;
      const scratch = core.scratch.current;
      const settled = moveItem(
        scratch.startLayout,
        gesture.id,
        cell.x,
        cell.y,
        core.columns,
        core.flow,
      );
      if (reflowHeldRef.current) {
        core.setPreview(settled);
        return;
      }
      // Siblings stay at their start positions; only the active item's rect
      // follows the (settled) landing cell so the target outline shows where
      // the tile will actually land on drop.
      const active = settled.find((i) => i.id === gesture.id);
      core.setPreview(
        active
          ? scratch.startLayout.map((i) => (i.id === gesture.id ? active : i))
          : settled,
      );
    },
    [core],
  );
  useReflowModifier(core, 'drag', reflowHeldRef, applyPreview);

  const onDragStart = React.useCallback(
    (event: DragStartEvent) => {
      if (!core.canManage) return;
      // Gesture exclusivity: one gesture at a time per grid. A keyboard-
      // sensor drag (Enter on a focused handle) must not replace an in-flight
      // marquee/resize — the foreign gesture's own end path would then
      // cancel/commit the wrong gesture.
      if (core.gestureRef.current != null) return;
      const id = String(event.active.id);
      const scratch = core.scratch.current;
      scratch.metrics = core.measure();
      scratch.startLayout = core.layoutRef.current;
      scratch.lastTarget = null;
      reflowHeldRef.current =
        core.liveReflow || reflowModifierHeld(event.activatorEvent);
      core.setGesture({
        id,
        mode: 'drag',
        origin: core.layoutRef.current.find((i) => i.id === id) ?? null,
        preview: null,
        area: null,
      });
    },
    [core],
  );

  const onDragMove = React.useCallback(
    (event: DragMoveEvent) => {
      const scratch = core.scratch.current;
      const metrics = scratch.metrics;
      if (!metrics || core.gestureRef.current?.mode !== 'drag') return;
      // Symmetric preview rule: a grid previews iff the drag's `over` zone
      // matches its own zone identity. Page grids (zoneId null) preview while
      // over no enabled zone; nested grids ONLY while over their own zone.
      // Outside that band the owning zone's grid holds the promise — drop the
      // cell preview so this grid promises nothing (the tile keeps following
      // the pointer via its translate). Re-entering re-establishes the
      // preview — the delta math below is stateless from startLayout.
      if (subgridZoneOver(event.over) !== core.zoneId) {
        if (scratch.lastTarget) {
          scratch.lastTarget = null;
          core.setPreview(null);
        }
        return;
      }
      const id = String(event.active.id);
      const start = scratch.startLayout.find((i) => i.id === id);
      if (!start) return;
      const stepX = Math.round(event.delta.x / pitchX(metrics));
      const stepY = Math.round(event.delta.y / pitchY(metrics));
      const targetX = clamp(
        start.x + stepX,
        0,
        Math.max(0, core.columns - start.w),
      );
      // Cap the row at "fully below every sibling" (or the tile's own row when
      // it already is the bottom-most). Rows past that are equivalent after
      // reflow, and an uncapped target lets auto-scroll keep growing the grid
      // — each scroll folds into the delta, pushing the preview lower, growing
      // the scroll range again, forever.
      const others = scratch.startLayout.filter((i) => i.id !== id);
      const maxY = Math.max(start.y, layoutBottom(others));
      const targetY = clamp(start.y + stepY, 0, maxY);
      // Only the snapped cell drives the reflow; sub-cell pointer motion is
      // carried by the translate transform. Skipping unchanged targets avoids
      // redundant reflows.
      const last = scratch.lastTarget;
      if (last && last.x === targetX && last.y === targetY) return;
      scratch.lastTarget = { x: targetX, y: targetY };
      applyPreview(scratch.lastTarget);
    },
    [core, applyPreview],
  );

  const onDragEnd = React.useCallback((event: DragEndEvent) => {
    // Only end a gesture this hook owns (mirror of the external hook's
    // guard): if the drag start was refused (exclusivity) while another
    // gesture is live, the stray end event must not cancel that gesture.
    if (core.gestureRef.current?.mode !== 'drag') return;
    // Drop ownership: exactly ONE grid commits — the one whose zone identity
    // matches the drop's `over` zone (page grids own null-zone drops). Every
    // other grid cancels without committing.
    if (subgridZoneOver(event.over) !== core.zoneId) {
      core.cancelGesture();
      return;
    }
    const gesture = core.gestureRef.current;
    // Without the live-reflow opt-in the preview never displaced siblings —
    // resolve the single authoritative reflow now, then commit it (setPreview
    // writes the gesture ref synchronously, so commitGesture sees it).
    if (gesture?.mode === 'drag' && gesture.preview && !reflowHeldRef.current) {
      const scratch = core.scratch.current;
      const cell = scratch.lastTarget;
      if (cell) {
        core.setPreview(
          moveItem(
            scratch.startLayout,
            gesture.id,
            cell.x,
            cell.y,
            core.columns,
            core.flow,
          ),
        );
      }
    }
    core.commitGesture();
  }, [core]);

  const onDragCancel = React.useCallback(() => {
    // Same ownership guard as onDragEnd — never cancel a foreign gesture.
    if (core.gestureRef.current?.mode !== 'drag') return;
    core.cancelGesture();
  }, [core]);

  return React.useMemo(
    () => ({
      onDragStart,
      onDragMove,
      onDragEnd,
      onDragCancel,
    }),
    [onDragStart, onDragMove, onDragEnd, onDragCancel],
  );
}
